import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { CloseCircle, SearchNormal1 } from 'iconsax-react';

import useApi from 'hooks/useApi';
import { diApi } from './api';
import type { CatalogueField, DataSourceSummary, SourceEntity } from './types';
import { entityLabel, entityNoun, errorText, formatCount, sourceTables } from './components/format';

interface Props {
  open: boolean;
  onClose: () => void;
  clientUid: string;
  source: DataSourceSummary | null;
  entity?: SourceEntity;
}

const TYPE_LABELS: Record<CatalogueField['type'], string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  time: 'Time',
  phone: 'Phone',
  flag: 'Flag'
};

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12 };

export default function FieldCatalogueDrawer({ open, onClose, clientUid, source, entity: initialEntity = 'loads' }: Props) {
  const { showToast } = useApi();
  const [entity, setEntity] = useState<SourceEntity>(initialEntity);
  const [fields, setFields] = useState<CatalogueField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) setEntity(initialEntity);
  }, [open, initialEntity]);

  useEffect(() => {
    if (!open || !source) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    diApi
      .getCatalogue(clientUid, source.uid, entity)
      .then((res) => {
        if (cancelled) return;
        if (res.success === false) {
          setError(res.message || 'Could not load the field catalogue.');
          setFields([]);
          return;
        }
        setFields(res.data ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = errorText(err, 'Could not load the field catalogue.');
        setError(msg);
        showToast(msg, 'error');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientUid, source?.uid, entity]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...fields].sort((a, b) => b.fillPct - a.fillPct || a.field.localeCompare(b.field));
    if (!q) return sorted;
    return sorted.filter((f) => f.field.toLowerCase().includes(q) || f.samples.some((s) => String(s).toLowerCase().includes(q)));
  }, [fields, search]);

  const alwaysEmpty = fields.filter((f) => !f.fillPct).length;
  const noun = entityNoun(entity);
  // Every table the source has sent; the one being shown is always offered, even before its first record.
  const tableNames = sourceTables(source).map((t) => t.name);
  const entityOptions = tableNames.includes(entity) ? tableNames : [entity, ...tableNames];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 720 } } }}>
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Box>
            <Typography variant="h5">Field catalogue · {entityLabel(entity)}</Typography>
            {source && (
              <Typography variant="caption" color="text.secondary">
                {source.name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Close field catalogue">
            <CloseCircle size={22} />
          </IconButton>
        </Stack>

        <Stack spacing={2} sx={{ px: 3, py: 2 }}>
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search fields or example values"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchNormal1 size={16} />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              select
              size="small"
              value={entity}
              onChange={(e) => setEntity(e.target.value as SourceEntity)}
              sx={{ minWidth: 150 }}
            >
              {entityOptions.map((e) => (
                <MenuItem key={e} value={e}>
                  {entityLabel(e)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {!loading && !error && fields.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {formatCount(fields.length)} fields arrive on Aljex {noun}s
              {alwaysEmpty ? `, and ${formatCount(alwaysEmpty)} of them are always empty` : ''}. Phone numbers are masked.
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>

        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress aria-label="Loading field catalogue" />
            </Box>
          ) : shown.length === 0 && !error ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {fields.length === 0 ? `No ${noun}s received yet, so there are no fields to show.` : 'No fields match your search.'}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell sx={{ width: 130 }}>Filled</TableCell>
                    <TableCell>Examples</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((f) => (
                    <TableRow key={f.field} hover>
                      <TableCell sx={{ ...mono, whiteSpace: 'nowrap' }}>
                        {f.field}
                        {f.group === 'extra_stops' && (
                          <Chip size="small" variant="outlined" label="Stops" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{TYPE_LABELS[f.type] ?? f.type}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.max(0, Math.min(100, f.fillPct))}
                            color={f.fillPct >= 90 ? 'success' : f.fillPct >= 40 ? 'primary' : f.fillPct > 0 ? 'warning' : 'inherit'}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" sx={{ minWidth: 34, textAlign: 'right' }}>
                            {Math.round(f.fillPct)}%
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {f.sensitive && f.samples.length === 0 ? (
                          <Typography variant="caption" color="text.disabled">
                            Hidden — never shown
                          </Typography>
                        ) : f.samples.length === 0 ? (
                          <Typography variant="caption" color="text.disabled">
                            Always empty
                          </Typography>
                        ) : (
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {f.samples.slice(0, 5).map((s, i) => (
                              <Box
                                key={`${f.field}-${i}`}
                                component="code"
                                sx={{
                                  ...mono,
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 1,
                                  bgcolor: 'grey.100',
                                  maxWidth: 220,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={String(s)}
                              >
                                {String(s)}
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
}
